import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { AvisContent } from "../client/HubAvis";
import { BookCallContent, MasterclassAdminView } from "../client/HubBookCall";
import { ClosingFeedbacks } from "../../components/ClosingFeedbacks";
import CrmBoard from "../../components/crm/CrmBoard";
import { useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, Lock, Unlock, CheckCircle2, Save, Archive, Plus, Trash2, Bell, CalendarClock, Mail, Eye, X, FileText, Upload, Maximize2, Minimize2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "../../lib/api";
import { StatusBadge } from "../../components/StatusBadge";
import { programFor, flattenItems, allSlugs } from "../client/programmeStructure";
import { PAGES } from "../client/programmeContent";
import { ProgrammePageView, SubmissionsProvider, AnnotatableText, AnnotateLayout } from "../client/ProgrammePage";
import { BlockComposer, BlocksView } from "../../components/Blocks";
import { ProgIcon } from "../../components/ProgIcon";

function rdvLabel(startIso, fallback) {
  if (!startIso) return fallback || "";
  const d = new Date(startIso);
  if (isNaN(d)) return fallback || "";
  const o = { timeZone: "Europe/Paris" };
  const cap = (s) => s.charAt(0).toUpperCase() + s.slice(1);
  const wd = new Intl.DateTimeFormat("fr-FR", { ...o, weekday: "short" }).format(d).replace(".", "");
  const day = new Intl.DateTimeFormat("fr-FR", { ...o, day: "numeric" }).format(d);
  const mon = new Intl.DateTimeFormat("fr-FR", { ...o, month: "short" }).format(d).replace(".", "");
  const time = new Intl.DateTimeFormat("fr-FR", { ...o, hour: "2-digit", minute: "2-digit" }).format(d).replace(":", "h");
  return `${cap(wd)}. ${day} ${mon}. · ${time}`;
}

function RowItem({ node, depth, locked, done, onToggle, onView }) {
  const hasContent = !!PAGES[node.slug];
  return (
    <div data-testid={`admin-resource-${node.slug}`} className="flex items-center gap-3 rounded-xl border border-line bg-white p-3" style={{ marginLeft: depth * 22 }}>
      <span className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-lg ${locked ? "bg-cream text-ink-muted" : "bg-bordeaux-tint text-bordeaux"}`}>
        {locked ? <Lock size={15} /> : <Unlock size={15} />}
      </span>
      <ProgIcon slug={node.slug} size="sm" />
      <div className="flex-1">
        <p className={`text-sm font-medium ${locked ? "text-ink-muted" : "text-ink"}`}>{node.label}</p>
        {done && <p className="text-xs text-[#065F46]">✅ Terminé par ta cliente</p>}
      </div>
      {hasContent && (
        <button data-testid={`admin-resource-view-${node.slug}`} onClick={() => onView(node)}
          className="flex shrink-0 items-center gap-1.5 rounded-full border border-bordeaux-border px-3.5 py-1.5 text-xs font-medium text-bordeaux transition hover:bg-bordeaux-tint">
          <Eye size={14} /> Voir ses réponses
        </button>
      )}
      <button data-testid={`admin-resource-toggle-${node.slug}`} onClick={() => onToggle(node.slug, locked)}
        className={`shrink-0 rounded-full px-4 py-1.5 text-xs font-medium transition ${locked ? "bg-bordeaux text-white hover:bg-bordeaux-dark" : "border border-line text-ink-soft hover:border-bordeaux hover:text-bordeaux"}`}>
        {locked ? "Débloquer" : "Bloquer"}
      </button>
    </div>
  );
}

const fmtDate = (d) => { if (!d) return "—"; const s = String(d).slice(0, 10); return /^\d{4}-\d{2}-\d{2}$/.test(s) ? new Date(s + "T00:00:00").toLocaleDateString("fr-FR", { day: "numeric", month: "short", year: "numeric" }) : d; };

function ProgressRing({ value }) {  const r = 34;
  const circ = 2 * Math.PI * r;
  const offset = circ - (value / 100) * circ;
  return (
    <div className="relative flex h-24 w-24 shrink-0 items-center justify-center" data-testid="client-progress-ring">
      <svg className="h-24 w-24 -rotate-90" viewBox="0 0 80 80">
        <circle cx="40" cy="40" r={r} fill="none" stroke="#F0E3E8" strokeWidth="7" />
        <circle cx="40" cy="40" r={r} fill="none" stroke="#7B113A" strokeWidth="7" strokeLinecap="round"
          strokeDasharray={circ} strokeDashoffset={offset} style={{ transition: "stroke-dashoffset 0.6s ease" }} />
      </svg>
      <span className="absolute font-sans text-xl font-medium text-bordeaux">{value}%</span>
    </div>
  );
}

function HubModal({ testid, eyebrow, title, subtitle, wide = false, onClose, children }) {
  const [big, setBig] = useState(false);
  const size = big ? "min-h-full max-w-none rounded-none sm:p-10" : `${wide ? "max-w-[1400px]" : "max-w-3xl"} rounded-2xl`;
  return createPortal(
    <div className={`fixed inset-0 z-[90] overflow-y-auto bg-ink/40 backdrop-blur-[2px] ${big ? "" : "p-4 py-10"}`} onClick={onClose}>
      <div data-testid={testid} data-expanded={big} className={`mx-auto w-full bg-cream p-6 shadow-xl transition-[max-width] duration-300 sm:p-8 ${size}`} onClick={(e) => e.stopPropagation()}>
        <div className="mb-6 flex items-start justify-between gap-4">
          <div>
            {eyebrow && <p className="eyebrow">{eyebrow}</p>}
            <h3 className="font-sans text-2xl font-medium text-ink">{title}</h3>
            {subtitle && <p className="mt-1 text-sm font-light text-ink-muted">{subtitle}</p>}
          </div>
          <div className="flex shrink-0 items-center gap-1">
            <button data-testid={`${testid}-expand`} title={big ? "Réduire" : "Ouvrir en grand"} onClick={() => setBig(!big)} className="flex items-center gap-1.5 rounded-full border border-line bg-white px-3 py-2 text-xs font-medium text-ink-soft transition hover:border-bordeaux hover:text-bordeaux">
              {big ? <Minimize2 size={15} /> : <Maximize2 size={15} />}<span className="hidden sm:inline">{big ? "Réduire" : "Ouvrir en grand"}</span>
            </button>
            <button data-testid={`${testid}-close`} onClick={onClose} className="rounded-full p-2 text-ink-muted hover:bg-white hover:text-bordeaux"><X size={20} /></button>
          </div>
        </div>
        {children}
      </div>
    </div>,
    document.body
  );
}


export default function ClientDossier() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [notes, setNotes] = useState("");
  const [tab, setTab] = useState("parcours");
  const [newTask, setNewTask] = useState("");
  const [hub, setHub] = useState([]);
  const [hubForm, setHubForm] = useState({ title: "", subtitle: "", blocks: [] });
  const [payments, setPayments] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [payForm, setPayForm] = useState({ label: "", date: "", amount: "" });
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [viewNode, setViewNode] = useState(null);
  const [arevoir, setArevoir] = useState(null);
  const [viewHub, setViewHub] = useState(null);
  const [viewAvis, setViewAvis] = useState(false);
  const [viewBook, setViewBook] = useState(false);
  const [viewClosing, setViewClosing] = useState(false);
  const [viewCrm, setViewCrm] = useState(false);
  const openContentSpace = () => navigate(`/admin/clientes/${id}/contenu`);

  const openArevoir = async () => {
    setArevoir(null);
    try {
      const { data } = await api.get(`/admin/clients/${id}/submissions/a-revoir`);
      let items = [];
      try { items = data?.items ? JSON.parse(data.items) : []; } catch (_) { items = []; }
      setArevoir(items);
    } catch (_) {
      setArevoir([]);
    }
  };
  useEffect(() => { if (tab === "arevoir" && arevoir === null) openArevoir(); }, [tab]);

  const load = () => api.get(`/admin/clients/${id}`).then(({ data }) => {
    setData(data);
    setNotes(data.client.private_notes || "");
  }).catch(() => {});
  const loadExtras = () => {
    api.get(`/admin/clients/${id}/hub-resources`).then(({ data }) => setHub(data)).catch(() => {});
    api.get(`/admin/clients/${id}/payments`).then(({ data }) => setPayments(data)).catch(() => {});
    api.get(`/admin/clients/${id}/invoices`).then(({ data }) => setInvoices(data)).catch(() => {});
  };
  useEffect(() => { load(); loadExtras(); }, [id]);

  if (!data) return <div className="text-ink-muted">Chargement…</div>;
  const { client, tasks, completed_slugs = [] } = data;
  const pendingTasks = (tasks || []).filter((t) => t.status !== "termine");
  const _all = allSlugs(client.programme_custom);
  const _done = completed_slugs.filter((s) => _all.includes(s));
  const progress = _all.length ? Math.round((_done.length / _all.length) * 100) : 0;
  const adjustWeek = async (delta) => { await api.post(`/admin/clients/${id}/week-adjust`, { delta }); load(); };

  const toggleContentSpace = async () => {
    const next = !client.content_space_locked;
    await api.put(`/admin/clients/${id}/content-space`, { locked: next });
    toast.success(next ? "Espace de création masqué pour ta cliente" : "Espace de création débloqué ✨");
    load();
  };

  const toggleAvisPage = async () => {
    const next = !client.avis_page_locked;
    await api.put(`/admin/clients/${id}/avis-page`, { locked: next });
    toast.success(next ? "Page masquée pour ta cliente" : "Page affichée ✨");
    load();
  };

  const toggleBookPage = async () => {
    const next = !client.booking_page_locked;
    await api.put(`/admin/clients/${id}/booking-page`, { locked: next });
    toast.success(next ? "Page masquée pour ta cliente" : "Page affichée ✨");
    load();
  };

  const toggleClosingPage = async () => {
    const next = !client.closing_page_locked;
    await api.put(`/admin/clients/${id}/closing-page`, { locked: next });
    toast.success(next ? "Page masquée pour ta cliente" : "Page affichée ✨");
    load();
  };

  const toggleCrmPage = async () => {
    const next = client.crm_page_locked === false;
    await api.put(`/admin/clients/${id}/crm-page`, { locked: next });
    toast.success(next ? "Page masquée pour ta cliente" : "Page affichée ✨");
    load();
  };

  const toggleResource = async (slug, locked) => {
    await api.post(`/admin/clients/${id}/resources/${slug}/${locked ? "unlock" : "lock"}`);
    toast.success(locked ? "Ressource débloquée ✨" : "Ressource bloquée");
    load();
  };
  const createHub = async () => {
    if (!hubForm.title.trim()) { toast.error("Ajoute un titre"); return; }
    await api.post(`/admin/clients/${id}/hub-resources`, hubForm);
    setHubForm({ title: "", subtitle: "", blocks: [] });
    toast.success("Ressource ajoutée au Hub ✨");
    loadExtras();
  };
  const toggleHub = async (r) => { await api.post(`/admin/hub-resources/${r.id}/toggle`); toast.success(r.locked ? "Affichée ✨" : "Masquée"); loadExtras(); };
  const deleteHub = async (r) => { await api.delete(`/admin/hub-resources/${r.id}`); toast.success("Ressource supprimée"); loadExtras(); };
  const addPayment = async () => {
    if (!payForm.label.trim()) { toast.error("Ajoute un libellé"); return; }
    await api.post(`/admin/clients/${id}/payments`, payForm);
    setPayForm({ label: "", date: "", amount: "" });
    toast.success("Versement ajouté");
    loadExtras();
  };
  const markPaid = async (p) => {
    await api.post(`/admin/clients/${id}/payments/${p.id}/mark-paid`);
    toast.success(p.status === "recu" ? "Remis à venir" : "Marqué payé ✅ — notification envoyée");
    loadExtras();
  };
  const remindPayment = async (p) => { await api.post(`/admin/clients/${id}/payments/${p.id}/remind`); toast.success("Rappel envoyé 💛"); loadExtras(); };
  const deletePayment = async (p) => { await api.delete(`/admin/clients/${id}/payments/${p.id}`); toast.success("Versement supprimé"); loadExtras(); };
  const uploadInvoice = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const fd = new FormData();
    fd.append("file", f);
    try {
      await api.post(`/admin/clients/${id}/invoices`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Facture ajoutée — ta cliente la voit maintenant 📄");
      loadExtras();
    } catch (_) { toast.error("Échec de l'envoi de la facture"); }
    e.target.value = "";
  };
  const deleteInvoice = async (inv) => { await api.delete(`/admin/clients/${id}/invoices/${inv.id}`); toast.success("Facture supprimée"); loadExtras(); };
  const saveNotes = async () => {
    await api.put(`/admin/clients/${id}/notes`, { private_notes: notes });
    toast.success("Notes privées enregistrées");
  };
  const archive = async () => {
    const { data: r } = await api.put(`/admin/clients/${id}/archive`);
    toast.success(r.archived ? "Cliente archivée" : "Cliente réactivée");
    load();
  };
  const resendInvite = async () => {
    try {
      await api.post(`/admin/clients/${id}/resend-invite`);
      toast.success(`Invitation renvoyée à ${client.email} 🤍`);
    } catch {
      toast.error("Impossible d'envoyer l'invitation pour le moment.");
    }
  };
  const removeClient = async () => {
    setDeleting(true);
    try {
      await api.delete(`/admin/clients/${id}`);
      toast.success("Espace de la cliente supprimé définitivement");
      navigate("/admin/clientes");
    } catch {
      toast.error("La suppression a échoué. Réessaie.");
      setDeleting(false);
    }
  };
  const addTask = async (e) => {
    e.preventDefault();
    if (!newTask.trim()) return;
    await api.post("/admin/tasks", { title: newTask, client_id: id, source: "module" });
    setNewTask("");
    toast.success("Tâche attribuée");
    load();
  };

  return (
    <div className="space-y-8">
      <button onClick={() => navigate("/admin/clientes")} className="flex items-center gap-2 text-sm text-ink-soft hover:text-bordeaux">
        <ArrowLeft size={16} /> Retour aux clientes
      </button>

      {/* Header */}
      <div className="flex flex-wrap items-center gap-6 rounded-2xl border border-line bg-white p-6 shadow-card">
        <img src={client.photo} alt={client.first_name} className="h-20 w-20 rounded-full object-cover ring-2 ring-bordeaux-border" />
        <div className="flex-1">
          <h1 className="font-sans font-medium text-3xl text-ink">{client.first_name} {client.last_name}</h1>
          <p className="text-ink-muted">{client.activity}</p>
          <button data-testid="view-as-client" onClick={() => window.open(`/espace?viewAs=${id}`, "_blank")} className="mb-1 mt-3 inline-flex items-center gap-2 rounded-full bg-bordeaux px-4 py-2 text-xs font-medium text-white shadow-[0_0_16px_rgba(123,17,58,0.45)] transition-transform hover:-translate-y-0.5"><Eye size={14} /> Vue de la cliente</button>
          <div className="mt-2 flex flex-wrap items-center gap-2">
            <span className="text-sm text-ink-soft">Semaine</span>
            <button data-testid="week-minus" onClick={() => adjustWeek(-1)} className="flex h-6 w-6 items-center justify-center rounded-full border border-line text-ink-soft transition hover:border-bordeaux hover:text-bordeaux">−</button>
            <span data-testid="current-week" className="text-sm font-semibold text-ink">{client.current_week} / {client.total_weeks}</span>
            <button data-testid="week-plus" onClick={() => adjustWeek(1)} className="flex h-6 w-6 items-center justify-center rounded-full border border-line text-ink-soft transition hover:border-bordeaux hover:text-bordeaux">+</button>
            <span className="text-sm text-ink-soft">· Phase {client.phase}</span>
          </div>
          <p className="mt-1 text-xs text-ink-muted">{fmtDate(client.start_date)} → {fmtDate(client.end_date)}{client.week_adjustment ? ` · ajusté de ${client.week_adjustment > 0 ? "+" : ""}${client.week_adjustment} sem.` : ""}</p>
        </div>
        <ProgressRing value={progress} />
        <div className="flex flex-col items-end gap-3">
          {client.status && <StatusBadge status={client.status} />}
          <button data-testid="resend-invite" onClick={resendInvite} className="flex items-center gap-2 rounded-full border border-line px-4 py-2 text-sm text-ink-soft hover:border-bordeaux hover:text-bordeaux">
            <Mail size={15} /> Renvoyer l'invitation
          </button>
          <button data-testid="archive-client" onClick={archive} className="flex items-center gap-2 rounded-full border border-line px-4 py-2 text-sm text-ink-soft hover:border-bordeaux hover:text-bordeaux">
            <Archive size={15} /> {client.archived ? "Réactiver" : "Archiver"}
          </button>
          <button data-testid="delete-client" onClick={() => setConfirmDelete(true)} className="flex items-center gap-2 rounded-full border border-red-500 bg-red-500 px-4 py-2 text-sm font-medium text-white transition hover:bg-red-600 hover:border-red-600">
            <Trash2 size={15} /> Supprimer l'espace
          </button>
        </div>
      </div>

      {/* Prochain RDV + mes tâches */}
      <div className="grid gap-4 sm:grid-cols-2">
        <div data-testid="dossier-next-rdv" className="rounded-2xl border border-bordeaux-border bg-bordeaux-tint/30 p-5">
          <div className="flex items-center gap-2 text-bordeaux">
            <CalendarClock size={18} />
            <p className="text-xs font-semibold uppercase tracking-wider">Prochain rendez-vous</p>
          </div>
          {client.next_call ? (
            <>
              <p data-testid="dossier-next-rdv-when" className="mt-1.5 font-sans text-sm font-light text-ink">{rdvLabel(client.next_call_data?.start_time, client.next_call)}</p>
              {client.next_call_data?.join_url && (
                <a href={client.next_call_data.join_url} target="_blank" rel="noopener noreferrer"
                  className="mt-2 inline-flex items-center gap-1.5 text-sm font-medium text-bordeaux underline underline-offset-4 hover:text-bordeaux-dark">
                  Rejoindre l'appel
                </a>
              )}
            </>
          ) : (
            <p className="mt-2 text-sm font-light text-ink-muted">Aucun rendez-vous planifié pour le moment.</p>
          )}
        </div>

        <div data-testid="dossier-my-tasks" className="rounded-2xl border border-bordeaux-border bg-bordeaux-tint/30 p-5">
          <div className="flex items-center justify-between">
            <p className="text-xs font-semibold uppercase tracking-wider text-ink-soft">Mes prochaines tâches</p>
            <button onClick={() => setTab("taches")} className="text-xs font-medium text-bordeaux hover:underline">Tout voir</button>
          </div>
          <div className="mt-3 space-y-2">
            {pendingTasks.length === 0 && <p className="text-sm font-light text-ink-muted">Aucune tâche en attente ✨</p>}
            {pendingTasks.slice(0, 4).map((t) => (
              <div key={t.id} className="flex items-center gap-2 text-sm text-ink">
                <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-bordeaux" />
                <span className="truncate">{t.title}</span>
              </div>
            ))}
            {pendingTasks.length > 4 && <p className="text-xs text-ink-muted">+{pendingTasks.length - 4} autre(s)</p>}
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex w-full gap-1 overflow-x-auto rounded-full bg-[#2D181E] p-1.5">
        {[["parcours", "Parcours"], ["hub", "Hub"], ["arevoir", "À revoir ensemble"], ["paiements", "Paiements"], ["taches", "Tâches"], ["notes", "Notes privées"]].map(([k, label]) => (
          <button
            key={k}
            data-testid={`dossier-tab-${k}`}
            onClick={() => setTab(k)}
            className={`flex-1 whitespace-nowrap rounded-full px-5 py-2.5 text-center text-sm transition-colors duration-200 ${tab === k ? "bg-bordeaux font-medium text-white shadow-[0_0_20px_rgba(123,17,58,0.6)]" : "text-white/70 hover:bg-white/5 hover:text-white"}`}
          >
            {label}
          </button>
        ))}
      </div>

      {tab === "parcours" && (
        <div className="space-y-10">
          <p className="rounded-xl border border-line bg-bordeaux-tint/30 px-4 py-3 text-sm font-light text-bordeaux">
            Bloque ou débloque chaque ressource — et même chaque sous-page — pour cette cliente. Clique sur <strong className="font-medium">« Voir ses réponses »</strong> pour lire tout ce qu'elle a rempli sur une page.
          </p>
          {programFor(client.programme_custom).map((ph) => (
            <div key={ph.phase}>
              <h2 className="mb-4 font-sans font-medium text-2xl text-ink">Phase {ph.phase} — {ph.title}</h2>
              <div className="space-y-6">
                {ph.groups.map((g, gi) => (
                  <div key={gi}>
                    {g.duration && <p className="mb-2 text-sm font-light italic text-ink-muted">{g.duration}</p>}
                    <div className="space-y-2">
                      {flattenItems(g.items).map(({ node, depth }) => (
                        <RowItem key={node.slug} node={node} depth={depth}
                          locked={(client.locked_resources || []).includes(node.slug)}
                          done={completed_slugs.includes(node.slug)} onToggle={toggleResource} onView={setViewNode} />
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      {tab === "taches" && (
        <div className="max-w-2xl space-y-4">
          <form onSubmit={addTask} className="flex gap-3">
            <input data-testid="dossier-new-task" value={newTask} onChange={(e) => setNewTask(e.target.value)}
              placeholder="Nouvelle tâche pour ta cliente…"
              className="flex-1 rounded-xl border border-line px-4 py-2.5 outline-none focus:border-bordeaux" />
            <button type="submit" className="flex items-center gap-2 rounded-full bg-bordeaux px-5 py-2.5 text-sm text-white hover:bg-bordeaux-dark">
              <Plus size={16} /> Ajouter
            </button>
          </form>
          <div className="space-y-2">
            {tasks.map((t) => (
              <div key={t.id} className="flex items-center justify-between rounded-xl border border-line bg-white p-4">
                <span className={`text-sm ${t.status === "termine" ? "text-ink-muted line-through" : "text-ink"}`}>{t.title}
                  {/^https?:\/\//.test(t.link || "") && <a data-testid={`dossier-task-link-${t.id}`} href={t.link} target="_blank" rel="noreferrer" className="ml-2 text-xs text-bordeaux hover:underline">{t.source === "closing" ? "Voir l'appel ↗" : "Voir sa page ↗"}</a>}
                </span>
                <span className="text-xs uppercase tracking-wider text-ink-muted">{t.status === "termine" ? "Terminé" : "À faire"}</span>
              </div>
            ))}
            {tasks.length === 0 && <p className="text-ink-muted">Aucune tâche attribuée.</p>}
          </div>
        </div>
      )}

      {tab === "hub" && (
        <div className="space-y-8">
          <p className="rounded-xl border border-line bg-bordeaux-tint/30 px-4 py-3 text-sm font-light text-bordeaux">
            Ajoute des ressources au Hub de ta cliente (texte, lien, vidéo, fichier) et affiche / masque-les quand tu veux.
          </p>
          <div data-testid="hub-content-space-row" className="rounded-2xl border border-line bg-white p-4 shadow-card">
            <div className="flex items-center gap-3">
              <span className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-lg ${client.content_space_locked ? "bg-cream text-ink-muted" : "bg-bordeaux-tint text-bordeaux"}`}>{client.content_space_locked ? <Lock size={15} /> : <Unlock size={15} />}</span>
              <div className="flex-1"><p className={`text-sm font-medium ${client.content_space_locked ? "text-ink-muted" : "text-ink"}`}>Espace de création de contenu</p><p className="text-xs text-ink-muted">Idées, calendrier, tracking et statistiques</p></div>
              <button data-testid="content-space-view" onClick={openContentSpace} className="flex shrink-0 items-center gap-1.5 rounded-full border border-bordeaux-border px-3.5 py-1.5 text-xs font-medium text-bordeaux transition hover:bg-bordeaux-tint"><Eye size={14} /> Voir</button>
              <button data-testid="hub-toggle-content-space" onClick={toggleContentSpace} className={`shrink-0 rounded-full px-4 py-1.5 text-xs font-medium transition ${client.content_space_locked ? "bg-bordeaux text-white hover:bg-bordeaux-dark" : "border border-line text-ink-soft hover:border-bordeaux hover:text-bordeaux"}`}>{client.content_space_locked ? "Afficher" : "Masquer"}</button>
            </div>
          </div>
          <div data-testid="hub-avis-row" className="-mt-5 rounded-2xl border border-line bg-white p-4 shadow-card">
            <div className="flex items-center gap-3">
              <span className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-lg ${client.avis_page_locked ? "bg-cream text-ink-muted" : "bg-bordeaux-tint text-bordeaux"}`}>{client.avis_page_locked ? <Lock size={15} /> : <Unlock size={15} />}</span>
              <div className="flex-1"><p className={`text-sm font-medium ${client.avis_page_locked ? "text-ink-muted" : "text-ink"}`}>Récolte des BONS avis (process)</p><p className="text-xs text-ink-muted">Avis pendant / fin d'accompagnement et étude de cas</p></div>
              <button data-testid="hub-avis-view" onClick={() => setViewAvis(true)} className="flex shrink-0 items-center gap-1.5 rounded-full border border-bordeaux-border px-3.5 py-1.5 text-xs font-medium text-bordeaux transition hover:bg-bordeaux-tint"><Eye size={14} /> Voir</button>
              <button data-testid="hub-toggle-avis" onClick={toggleAvisPage} className={`shrink-0 rounded-full px-4 py-1.5 text-xs font-medium transition ${client.avis_page_locked ? "bg-bordeaux text-white hover:bg-bordeaux-dark" : "border border-line text-ink-soft hover:border-bordeaux hover:text-bordeaux"}`}>{client.avis_page_locked ? "Afficher" : "Masquer"}</button>
            </div>
          </div>
          <div data-testid="hub-bookcall-row" className="-mt-5 rounded-2xl border border-line bg-white p-4 shadow-card">
            <div className="flex items-center gap-3">
              <span className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-lg ${client.booking_page_locked ? "bg-cream text-ink-muted" : "bg-bordeaux-tint text-bordeaux"}`}>{client.booking_page_locked ? <Lock size={15} /> : <Unlock size={15} />}</span>
              <div className="flex-1"><p className={`text-sm font-medium ${client.booking_page_locked ? "text-ink-muted" : "text-ink"}`}>Tutoriel : Comment créer ta page book a call</p><p className="text-xs text-ink-muted">Rédaction, design, mise en place et derniers détails</p></div>
              <button data-testid="hub-bookcall-view" onClick={() => setViewBook(true)} className="flex shrink-0 items-center gap-1.5 rounded-full border border-bordeaux-border px-3.5 py-1.5 text-xs font-medium text-bordeaux transition hover:bg-bordeaux-tint"><Eye size={14} /> Voir</button>
              <button data-testid="hub-toggle-bookcall" onClick={toggleBookPage} className={`shrink-0 rounded-full px-4 py-1.5 text-xs font-medium transition ${client.booking_page_locked ? "bg-bordeaux text-white hover:bg-bordeaux-dark" : "border border-line text-ink-soft hover:border-bordeaux hover:text-bordeaux"}`}>{client.booking_page_locked ? "Afficher" : "Masquer"}</button>
            </div>
          </div>
          <div data-testid="hub-closing-row" className="-mt-5 rounded-2xl border border-line bg-white p-4 shadow-card">
            <div className="flex items-center gap-3">
              <span className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-lg ${client.closing_page_locked ? "bg-cream text-ink-muted" : "bg-bordeaux-tint text-bordeaux"}`}>{client.closing_page_locked ? <Lock size={15} /> : <Unlock size={15} />}</span>
              <div className="flex-1"><p className={`text-sm font-medium ${client.closing_page_locked ? "text-ink-muted" : "text-ink"}`}>Feedback de tes calls de closing</p><p className="text-xs text-ink-muted">Ses liens d'appels et tes retours</p></div>
              <button data-testid="hub-closing-view" onClick={() => setViewClosing(true)} className="flex shrink-0 items-center gap-1.5 rounded-full border border-bordeaux-border px-3.5 py-1.5 text-xs font-medium text-bordeaux transition hover:bg-bordeaux-tint"><Eye size={14} /> Ouvrir</button>
              <button data-testid="hub-toggle-closing" onClick={toggleClosingPage} className={`shrink-0 rounded-full px-4 py-1.5 text-xs font-medium transition ${client.closing_page_locked ? "bg-bordeaux text-white hover:bg-bordeaux-dark" : "border border-line text-ink-soft hover:border-bordeaux hover:text-bordeaux"}`}>{client.closing_page_locked ? "Afficher" : "Masquer"}</button>
            </div>
          </div>
          <div data-testid="hub-crm-row" className="-mt-5 rounded-2xl border border-line bg-white p-4 shadow-card">
            <div className="flex items-center gap-3">
              <span className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-lg ${client.crm_page_locked !== false ? "bg-cream text-ink-muted" : "bg-bordeaux-tint text-bordeaux"}`}>{client.crm_page_locked !== false ? <Lock size={15} /> : <Unlock size={15} />}</span>
              <div className="flex-1"><p className={`text-sm font-medium ${client.crm_page_locked !== false ? "text-ink-muted" : "text-ink"}`}>Ton CRM de setting</p><p className="text-xs text-ink-muted">Ses prospects, relances et ventes</p></div>
              <button data-testid="hub-crm-view" onClick={() => setViewCrm(true)} className="flex shrink-0 items-center gap-1.5 rounded-full border border-bordeaux-border px-3.5 py-1.5 text-xs font-medium text-bordeaux transition hover:bg-bordeaux-tint"><Eye size={14} /> Ouvrir</button>
              <button data-testid="hub-toggle-crm" onClick={toggleCrmPage} className={`shrink-0 rounded-full px-4 py-1.5 text-xs font-medium transition ${client.crm_page_locked !== false ? "bg-bordeaux text-white hover:bg-bordeaux-dark" : "border border-line text-ink-soft hover:border-bordeaux hover:text-bordeaux"}`}>{client.crm_page_locked !== false ? "Afficher" : "Masquer"}</button>
            </div>
          </div>
          <div className="space-y-3">
            {hub.map((r) => (
              <div key={r.id} data-testid={`hub-resource-${r.id}`} className="rounded-2xl border border-line bg-white p-4 shadow-card">
                <div className="flex items-center gap-3">
                  <span className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-lg ${r.locked ? "bg-cream text-ink-muted" : "bg-bordeaux-tint text-bordeaux"}`}>{r.locked ? <Lock size={15} /> : <Unlock size={15} />}</span>
                  <div className="flex-1"><p className={`text-sm font-medium ${r.locked ? "text-ink-muted" : "text-ink"}`}>{r.title}</p><p className="text-xs text-ink-muted">{r.subtitle}</p></div>
                  <button data-testid={`hub-view-${r.id}`} onClick={() => setViewHub(r)} className="flex shrink-0 items-center gap-1.5 rounded-full border border-bordeaux-border px-3.5 py-1.5 text-xs font-medium text-bordeaux transition hover:bg-bordeaux-tint"><Eye size={14} /> Voir</button>
                  <button data-testid={`hub-toggle-${r.id}`} onClick={() => toggleHub(r)} className={`shrink-0 rounded-full px-4 py-1.5 text-xs font-medium transition ${r.locked ? "bg-bordeaux text-white hover:bg-bordeaux-dark" : "border border-line text-ink-soft hover:border-bordeaux hover:text-bordeaux"}`}>{r.locked ? "Afficher" : "Masquer"}</button>
                  <button data-testid={`hub-delete-${r.id}`} onClick={() => deleteHub(r)} className="shrink-0 text-ink-muted hover:text-bordeaux"><Trash2 size={16} /></button>
                </div>
              </div>
            ))}
            {hub.length === 0 && <p className="text-ink-muted">Aucune ressource ajoutée pour le moment.</p>}
          </div>
          <div className="rounded-2xl border border-line bg-white p-5 shadow-card">
            <p className="mb-3 font-sans text-lg font-medium text-ink">Ajouter une ressource</p>
            <div className="space-y-3">
              <input data-testid="hub-form-title" value={hubForm.title} onChange={(e) => setHubForm({ ...hubForm, title: e.target.value })} placeholder="Titre de la ressource" className="w-full rounded-xl border border-line px-4 py-2.5 outline-none focus:border-bordeaux" />
              <input data-testid="hub-form-subtitle" value={hubForm.subtitle} onChange={(e) => setHubForm({ ...hubForm, subtitle: e.target.value })} placeholder="Sous-titre (optionnel)" className="w-full rounded-xl border border-line px-4 py-2.5 outline-none focus:border-bordeaux" />
              <BlockComposer blocks={hubForm.blocks} onChange={(b) => setHubForm({ ...hubForm, blocks: b })} />
              <button data-testid="hub-create" onClick={createHub} className="flex items-center gap-2 rounded-full bg-bordeaux px-5 py-2.5 text-sm text-white hover:bg-bordeaux-dark"><Plus size={16} /> Ajouter au Hub</button>
            </div>
          </div>
        </div>
      )}

      {tab === "paiements" && (
        <div className="space-y-8">
          <p className="rounded-xl border border-line bg-bordeaux-tint/30 px-4 py-3 text-sm font-light text-bordeaux">
            Marque les versements reçus et envoie des rappels. Ta cliente reçoit une notification, et toi aussi.
          </p>
          <div className="flex items-center justify-between rounded-2xl border border-line bg-white px-5 py-4 shadow-card">
            <span className="text-sm text-ink-soft">Paiements reçus</span>
            <span data-testid="admin-payment-progress" className="text-sm font-semibold text-ink">{payments.filter((p) => p.status === "recu").length} / {payments.length}</span>
          </div>
          <div className="space-y-3">
            {payments.map((p) => (
              <div key={p.id} data-testid={`payment-${p.id}`} className="rounded-2xl border border-line bg-white p-4 shadow-card">
                <div className="flex flex-wrap items-center gap-3">
                  <span className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-lg ${p.status === "recu" ? "bg-[#EAF5EC] text-[#2E7D46]" : "bg-cream text-ink-muted"}`}>
                    {p.status === "recu" ? <CheckCircle2 size={17} /> : <CalendarClock size={17} />}
                  </span>
                  <div className="min-w-[150px] flex-1">
                    <p className={`text-sm font-medium ${p.status === "recu" ? "text-ink" : "text-ink"}`}>{p.label}{p.amount ? ` · ${p.amount}` : ""}</p>
                    <p className="text-xs text-ink-muted">{p.date ? fmtDate(p.date) : "Date non définie"}{p.status === "recu" ? " · Reçu" : ""}</p>
                  </div>
                  <button data-testid={`payment-mark-${p.id}`} onClick={() => markPaid(p)} className={`shrink-0 rounded-full px-4 py-1.5 text-xs font-medium transition ${p.status === "recu" ? "border border-line text-ink-soft hover:border-bordeaux hover:text-bordeaux" : "bg-[#2E7D46] text-white hover:opacity-90"}`}>
                    {p.status === "recu" ? "Marquer à venir" : "Marquer payé"}
                  </button>
                  <button data-testid={`payment-remind-${p.id}`} onClick={() => remindPayment(p)} className="flex shrink-0 items-center gap-1 rounded-full border border-line px-3 py-1.5 text-xs text-ink-soft transition hover:border-bordeaux hover:text-bordeaux">
                    <Bell size={13} /> Rappel
                  </button>
                  <button data-testid={`payment-delete-${p.id}`} onClick={() => deletePayment(p)} className="shrink-0 text-ink-muted hover:text-bordeaux"><Trash2 size={15} /></button>
                </div>
              </div>
            ))}
            {payments.length === 0 && <p className="text-ink-muted">Aucun versement défini.</p>}
          </div>
          <div className="rounded-2xl border border-line bg-white p-5 shadow-card">
            <p className="mb-3 font-sans text-lg font-medium text-ink">Ajouter un versement</p>
            <div className="grid gap-3 sm:grid-cols-3">
              <input data-testid="payment-form-label" value={payForm.label} onChange={(e) => setPayForm({ ...payForm, label: e.target.value })} placeholder="Libellé (ex. Versement 1)" className="w-full rounded-xl border border-line px-4 py-2.5 text-sm outline-none focus:border-bordeaux" />
              <input data-testid="payment-form-date" type="date" value={payForm.date} onChange={(e) => setPayForm({ ...payForm, date: e.target.value })} className="w-full rounded-xl border border-line px-4 py-2.5 text-sm text-ink-soft outline-none focus:border-bordeaux" />
              <input data-testid="payment-form-amount" value={payForm.amount} onChange={(e) => setPayForm({ ...payForm, amount: e.target.value })} placeholder="Montant (ex. 500 €)" className="w-full rounded-xl border border-line px-4 py-2.5 text-sm outline-none focus:border-bordeaux" />
            </div>
            <button data-testid="payment-add" onClick={addPayment} className="mt-3 flex items-center gap-2 rounded-full bg-bordeaux px-5 py-2.5 text-sm text-white hover:bg-bordeaux-dark"><Plus size={16} /> Ajouter</button>
          </div>

          <div className="rounded-2xl border border-line bg-white p-5 shadow-card" data-testid="admin-invoices">
            <div className="mb-1 flex items-center gap-2">
              <FileText size={18} className="text-bordeaux" />
              <p className="font-sans text-lg font-medium text-ink">Facture</p>
            </div>
            <p className="mb-4 text-sm font-light text-ink-muted">Ajoute la facture de ta cliente (PDF ou image). Elle apparaîtra directement dans son espace.</p>
            <div className="space-y-2">
              {invoices.map((inv) => (
                <div key={inv.id} data-testid={`invoice-${inv.id}`} className="flex items-center gap-3 rounded-xl border border-line bg-cream/40 p-3">
                  <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-bordeaux-tint text-bordeaux"><FileText size={16} /></span>
                  <a href={inv.url} target="_blank" rel="noreferrer" download={inv.name} className="flex-1 truncate text-sm font-medium text-bordeaux underline underline-offset-4">{inv.name}</a>
                  <button data-testid={`invoice-delete-${inv.id}`} onClick={() => deleteInvoice(inv)} className="shrink-0 text-ink-muted transition hover:text-bordeaux"><Trash2 size={15} /></button>
                </div>
              ))}
              {invoices.length === 0 && <p className="text-sm text-ink-muted">Aucune facture ajoutée pour l'instant.</p>}
            </div>
            <label className="mt-4 flex w-fit cursor-pointer items-center gap-2 rounded-full bg-bordeaux px-5 py-2.5 text-sm text-white transition hover:bg-bordeaux-dark">
              <Upload size={16} /> Ajouter une facture
              <input type="file" accept="application/pdf,image/*" onChange={uploadInvoice} data-testid="invoice-upload" className="hidden" />
            </label>
          </div>
        </div>
      )}

      {tab === "notes" && (
        <div className="max-w-2xl">
          <p className="mb-2 text-sm text-ink-soft">Ces notes sont <strong>strictement privées</strong> et invisibles pour ta cliente.</p>
          <textarea
            data-testid="private-notes"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            rows={10}
            className="w-full rounded-2xl border border-line bg-white p-4 outline-none focus:border-bordeaux"
            placeholder="Tes observations sur cette cliente…"
          />
          <button data-testid="save-notes" onClick={saveNotes} className="mt-3 flex items-center gap-2 rounded-full bg-bordeaux px-5 py-2.5 text-sm text-white hover:bg-bordeaux-dark">
            <Save size={16} /> Enregistrer
          </button>
        </div>
      )}

      {viewNode && createPortal(
        <div className="fixed inset-0 z-[90] flex items-start justify-center overflow-y-auto bg-ink/40 p-4 py-10 backdrop-blur-[2px]" onClick={() => setViewNode(null)}>
          <div data-testid="submission-viewer" className="w-full max-w-5xl rounded-2xl bg-cream p-6 shadow-editorial sm:p-8" onClick={(e) => e.stopPropagation()}>
            <div className="mb-6 flex items-center justify-between gap-4">
              <div>
                <p className="eyebrow">Réponses de {client.first_name}</p>
                <h2 className="font-sans text-2xl font-medium text-ink">{viewNode.label}</h2>
                <p className="mt-1 text-xs font-light text-ink-muted">Tu peux modifier et mettre en forme (gras, couleur, surlignage). Tes retouches sont enregistrées automatiquement.</p>
              </div>
              <button data-testid="submission-viewer-close" onClick={() => setViewNode(null)} className="flex h-9 w-9 items-center justify-center rounded-full border border-line text-ink-soft transition hover:border-bordeaux hover:text-bordeaux">
                <X size={18} />
              </button>
            </div>
            <ProgrammePageView slug={viewNode.slug} clientId={id} clientName={client.first_name} annotate />
          </div>
        </div>,
        document.body
      )}

      {tab === "arevoir" && (
        <div className="space-y-4" data-testid="arevoir-viewer">
          <div>
            <p className="eyebrow">À revoir ensemble</p>
            <h2 className="font-sans text-2xl font-medium text-ink">Ce que {client.first_name} veut revoir</h2>
          </div>
          {arevoir === null && <p className="text-sm text-ink-muted">Chargement…</p>}
          {arevoir !== null && arevoir.length === 0 && (
            <p className="rounded-xl border border-dashed border-line bg-white px-4 py-8 text-center text-sm italic text-ink-muted">Ta cliente n'a rien ajouté ici pour le moment.</p>
          )}
          {arevoir !== null && arevoir.length > 0 && (
            <SubmissionsProvider page="a-revoir" clientId={id} annotate readOnly>
              <p className="mb-3 text-xs font-light text-ink-muted">Sélectionne un passage pour le mettre en forme ou le commenter.</p>
              <AnnotateLayout>
                <div className="space-y-3">
                  {arevoir.map((it) => (
                    <AnnotatableText key={it.id} storageKey={`arev-${it.id}`} fallback={it.text} />
                  ))}
                </div>
              </AnnotateLayout>
            </SubmissionsProvider>
          )}
        </div>
      )}

      {viewAvis && <HubModal testid="avis-modal" title="Récolte des BONS avis (process)" onClose={() => setViewAvis(false)}><AvisContent /></HubModal>}
      {viewCrm && <HubModal testid="crm-modal" title="Son CRM de setting" wide onClose={() => setViewCrm(false)}><CrmBoard clientId={id} /></HubModal>}
      {viewClosing && <HubModal testid="closing-modal" title="Ses calls de closing" onClose={() => setViewClosing(false)}><ClosingFeedbacks clientId={id} /></HubModal>}
      {viewBook && <HubModal testid="bookcall-modal" title="Tutoriel : Comment créer ta page book a call (étape par étape)" onClose={() => setViewBook(false)}><MasterclassAdminView clientId={id} /><BookCallContent /></HubModal>}
      {viewHub && <HubModal testid="hub-viewer" eyebrow="Ressource du Hub" title={viewHub.title} subtitle={viewHub.subtitle} onClose={() => setViewHub(null)}>
            {(!viewHub.blocks || viewHub.blocks.length === 0)
              ? <p className="rounded-xl border border-dashed border-line bg-white px-4 py-8 text-center text-sm italic text-ink-muted">Cette ressource n'a pas encore de contenu.</p>
              : <BlocksView blocks={viewHub.blocks} />}
      </HubModal>}

      {confirmDelete && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-ink/40 p-4">
          <div className="w-full max-w-md rounded-2xl bg-white p-8 shadow-editorial">
            <div className="flex items-center gap-3">
              <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-red-100 text-red-600"><Trash2 size={18} /></span>
              <h2 className="font-sans font-medium text-2xl text-ink">Supprimer l'espace</h2>
            </div>
            <p className="mt-4 text-sm font-light text-ink-soft">
              Tu es sur le point de supprimer définitivement l'espace de <strong className="font-medium text-ink">{client.first_name} {client.last_name}</strong>.
              Toutes ses données (progression, exercices, tâches, paiements) seront effacées. <strong className="font-medium text-red-600">Cette action est irréversible.</strong>
            </p>
            <div className="mt-6 flex gap-3">
              <button data-testid="cancel-delete" onClick={() => setConfirmDelete(false)} disabled={deleting}
                className="flex-1 rounded-full border border-line py-3 text-sm text-ink-soft transition hover:border-bordeaux hover:text-bordeaux disabled:opacity-60">
                Annuler
              </button>
              <button data-testid="confirm-delete" onClick={removeClient} disabled={deleting}
                className="flex-1 rounded-full bg-red-500 py-3 text-sm font-medium text-white transition hover:bg-red-600 disabled:opacity-60">
                {deleting ? "Suppression…" : "Supprimer définitivement"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
